import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import {
  ConfirmationProof,
  TwoFactorStatus,
  disableTwoFactor,
  fetchTwoFactorStatus,
  regenerateBackupCodes,
} from "@/api/twoFactor";
import { AccountCard, AccountShell } from "@/components/layouts";
import { Button } from "@/components/ui";
import { headingVariants } from "@/components/ui/Heading";
import { InlineError } from "@/components/ui/InlineError";
import { Notice } from "@/components/ui/Notice";
import { StatusChip } from "@/components/ui/StatusChip";
import { TextLink } from "@/components/ui/TextLink";
import { SUPPORT_EMAIL } from "@/config/support";
import { useAuthContext } from "@/context/auth";
import { formatDate } from "@/lib/format";
import { resolveReturnTarget } from "@/lib/return-target";

import { BackupCodesPanel } from "./BackupCodesPanel";
import { ConfirmIdentityDialog } from "./ConfirmIdentityDialog";
import { describeError } from "./errorCopy";
import { SecurityOutcome, readOutcome } from "./securityOutcome";
import { supportResetHref } from "./supportReset";

type OpenDialog = "regenerate" | "disable" | null;

const TOTAL_BACKUP_CODES = 10;
/** At or below this many codes the count stops being a quiet line and becomes a warning */
const LOW_BACKUP_CODES = 3;

/**
 * Status-only landing page for two-step verification: what it is, and links to the two things a
 * person can do about it. Both regenerating codes and turning it off confirm in
 * `ConfirmIdentityDialog` rather than through inline forms, so exactly one proof field is ever on
 * screen, next to the sentence describing what it is about to do.
 *
 * It also says how a journey ended: setup arrives with an "on" notice through router state, and
 * turning it off raises an "off" one here. The notice takes focus - the control that started the
 * change is gone by then - and so do freshly issued backup codes.
 */
export const AccountSecurityView = () => {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  const { user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<SecurityOutcome | null>(() => readOutcome(location.state));
  const noticeRef = useRef<HTMLElement>(null);
  const codesRef = useRef<HTMLElement>(null);
  // The outcome whose notice has already taken focus, so a later status reload does not pull focus
  // back to it
  const focusedOutcome = useRef<SecurityOutcome | null>(null);

  // A notice is shown only once the status is known, and only when it agrees with the status chip:
  // "on" over a card that says Off (a hand-made or stale router state) would contradict itself
  const noticeOutcome =
    outcome && !codes && status && outcome === (status.enabled ? "enabled" : "disabled")
      ? outcome
      : null;

  const reload = () => fetchTwoFactorStatus().then(setStatus);

  // Also what Retry runs: clearing the error first puts the loading line back while it tries again
  const load = () => {
    setError("");
    reload().catch(() => setError("Could not load your two-step verification settings."));
  };

  useEffect(load, []);

  // Router state rides on the history entry and survives a reload, so the outcome is read once and
  // then wiped: reloading the page must not announce it again.
  useEffect(() => {
    if (readOutcome(location.state)) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
    }
    // Only the arrival matters; the wipe itself changes `location`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The proof dialog's own focus return lands on a button that no longer exists once the status
  // flips, so the notice that reports the change takes focus instead.
  // It does so once per outcome: the status reloading again (new backup codes, a refresh) is not a
  // reason to move focus again, and the memory resets when the outcome clears.
  useEffect(() => {
    if (!outcome) {
      focusedOutcome.current = null;
      return;
    }
    if (noticeOutcome && focusedOutcome.current !== noticeOutcome) {
      focusedOutcome.current = noticeOutcome;
      noticeRef.current?.focus();
    }
  }, [outcome, noticeOutcome]);

  useEffect(() => {
    if (codes) {
      codesRef.current?.focus();
    }
  }, [codes]);

  // Opening or cancelling a dialog always starts from a clean slate: an error left over from a
  // previous attempt - in this dialog or the other one - must never carry over.
  const openDialog = (which: OpenDialog) => {
    setError("");
    setOutcome(null);
    setDialog(which);
  };

  const closeDialog = () => {
    setError("");
    setDialog(null);
  };

  // The reload is deliberately outside the try that guards the mutating call. If the call itself
  // succeeds but the status refresh that follows fails, that must read as a refresh problem, not
  // as the action having failed - the change already happened and must not be reported as lost,
  // and whatever is already on screen (the freshly issued codes) must stay there.
  const regenerate = async (proof: ConfirmationProof) => {
    setError("");
    let newCodes: string[];
    try {
      newCodes = await regenerateBackupCodes(proof);
    } catch (e) {
      setError(describeError(e, status?.method ?? undefined));
      return;
    }
    setCodes(newCodes);
    setDialog(null);
    try {
      await reload();
    } catch {
      setError(
        "Your new backup codes were issued, but the page could not refresh. Reload to see the latest status.",
      );
    }
  };

  const turnOff = async (proof: ConfirmationProof) => {
    setError("");
    try {
      await disableTwoFactor(proof);
    } catch (e) {
      setError(describeError(e, status?.method ?? undefined));
      return;
    }
    setDialog(null);
    try {
      await reload();
      // Only once the card shows the new state: a notice over a card that still says ON would
      // contradict itself, and a failed refresh has its own message below.
      setOutcome("disabled");
    } catch {
      setError(
        "Two-step verification was turned off, but the page could not refresh. Reload to see the latest status.",
      );
    }
  };

  const returnTarget = resolveReturnTarget(document.referrer, user?.products ?? []);

  if (!status) {
    return (
      <AccountShell title="Account security">
        {error ? (
          <InlineError onRetry={load}>{error}</InlineError>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Loading your security settings…
          </p>
        )}
      </AccountShell>
    );
  }

  return (
    <AccountShell title="Account security">
      {error && !dialog && (
        <div className="mb-4">
          <InlineError>{error}</InlineError>
        </div>
      )}

      {noticeOutcome && (
        <Notice
          ref={noticeRef}
          tabIndex={-1}
          tone="success"
          className="mb-6"
          action={
            noticeOutcome === "enabled" ? (
              // The header already links back; the button is for widths where that link is far away
              <Button
                asChild
                variant="outline"
                size="sm"
                className="hidden pointer-coarse:min-h-11 sm:inline-flex"
              >
                <a href={returnTarget.url}>Back to {returnTarget.label}</a>
              </Button>
            ) : undefined
          }
        >
          {noticeOutcome === "enabled"
            ? "Two-step verification is on."
            : "Two-step verification is off. We've emailed you a confirmation."}
        </Notice>
      )}

      {codes && (
        <AccountCard>
          <BackupCodesPanel
            ref={codesRef}
            codes={codes}
            onAcknowledge={() => setCodes(null)}
            acknowledgeLabel="I've saved these codes"
          />
        </AccountCard>
      )}

      {!codes && (
        <AccountCard className="flex flex-col gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h2 className={headingVariants({ variant: "title" })}>Two-step verification</h2>
              <StatusChip tone={status.enabled ? "success" : "neutral"}>
                {status.enabled ? "On" : "Off"}
              </StatusChip>
            </div>
            {status.enabled ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {status.method === "EMAIL"
                    ? "A code sent to your email address is required when you sign in."
                    : "A code from your authenticator app is required when you sign in."}
                </p>
                {status.activatedAt && (
                  <p className="text-sm text-muted-foreground">
                    Turned on {formatDate(status.activatedAt)}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Even if someone learns your password, they can't sign in without a code from an
                authenticator app or your email.
              </p>
            )}
          </div>

          {status.enabled ? (
            <>
              <div className="space-y-3 border-t pt-6">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className={headingVariants({ variant: "group" })}>Backup codes</h3>
                  {status.backupCodesRemaining > LOW_BACKUP_CODES && (
                    <p className="text-sm text-muted-foreground">
                      {status.backupCodesRemaining} of {TOTAL_BACKUP_CODES} left
                    </p>
                  )}
                </div>
                {status.backupCodesRemaining <= LOW_BACKUP_CODES ? (
                  <Notice
                    tone="warning"
                    // The notice's text column has no minimum width, so on a phone a side-by-side
                    // action squeezes it to a letter a line. A full-width action wraps below it.
                    action={
                      <div className="w-full sm:w-auto">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full pointer-coarse:min-h-11 sm:w-auto"
                          onClick={() => openDialog("regenerate")}
                        >
                          Generate new backup codes
                        </Button>
                      </div>
                    }
                  >
                    {status.backupCodesRemaining === 0 ? (
                      <>
                        <span className="font-semibold">You have no backup codes left.</span>{" "}
                        Generate a new set so you can still sign in when you can't get a code.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">
                          Only {status.backupCodesRemaining} backup{" "}
                          {status.backupCodesRemaining === 1 ? "code" : "codes"} left.
                        </span>{" "}
                        Generate a new set before they run out.
                      </>
                    )}
                  </Notice>
                ) : (
                  <Button
                    type="button"
                    size="xl"
                    variant="outline"
                    className="w-full"
                    onClick={() => openDialog("regenerate")}
                  >
                    Generate new backup codes
                  </Button>
                )}
              </div>
              <div className="border-t pt-6">
                <button
                  type="button"
                  className="inline-flex items-center text-sm text-muted-foreground underline hover:text-destructive-fg pointer-coarse:min-h-11"
                  onClick={() => openDialog("disable")}
                >
                  Turn off two-step verification
                </button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Can't get a code? Email{" "}
                  <TextLink
                    href={supportResetHref(user?.username)}
                    className="text-xs font-semibold"
                  >
                    {SUPPORT_EMAIL}
                  </TextLink>{" "}
                  and ask for a reset of two-step verification.
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <Button asChild variant="brand" size="xl" className="w-full">
                <Link to="/security/two-factor/setup/method">Set it up</Link>
              </Button>
              <p className="text-center text-sm text-muted-foreground">Takes about 2 minutes</p>
            </div>
          )}
        </AccountCard>
      )}

      <ConfirmIdentityDialog
        isOpen={dialog === "regenerate"}
        title="Generate new backup codes?"
        consequence="Your current backup codes stop working immediately."
        confirmLabel="Generate"
        method={status.method ?? "TOTP"}
        passwordAvailable={status.passwordConfirmationAvailable}
        error={dialog === "regenerate" ? error : undefined}
        onConfirm={regenerate}
        onCancel={closeDialog}
      />

      <ConfirmIdentityDialog
        isOpen={dialog === "disable"}
        title="Turn off two-step verification?"
        consequence="You'll sign in without a second step. Your backup codes stop working."
        confirmLabel="Turn off"
        destructive
        method={status.method ?? "TOTP"}
        passwordAvailable={status.passwordConfirmationAvailable}
        error={dialog === "disable" ? error : undefined}
        onConfirm={turnOff}
        onCancel={closeDialog}
      />
    </AccountShell>
  );
};
